// ADR 28.9.26d §1 — one fixed region in the shell, a leaf and not a provider.
//
// It reads the module store through `useSyncExternalStore` and renders:
//
// - a **pinned** slot first (`UpdateToast`, which shares the look and the
//   column but not the queue: it is indefinite by design, and an indefinite
//   queue head would block every snackbar behind it);
// - one `sr-only` polite live region, mounted with the host and never
//   unmounted, whose text *changes* per entry. A live region inserted together
//   with its text is not reliably announced. No `role="status"`: the harness
//   mounts a host under every page suite, and those suites count the page's
//   own status elements;
// - the current entry's visible surface, keyed on its id so `animate-reveal`
//   replays per entry while the live region stays put. Its message is
//   `aria-hidden` — the live region already speaks it.
//
// Placement clears the tutor FAB's lane at every class (FAB top + 0.5rem), as
// Android's `Scaffold` lifts a snackbar above its FAB. ADR 29.9.26al §4 — the
// host is mounted inside `AdaptiveShell`'s `WindowClassProvider` and reads the
// class, so no `sm:` screen remains: centred above the bar at compact, centred
// clear of the rail at medium, and bottom-start beside the sidebar at expanded.
//
// One timer per current entry, cleared and restarted in full while the pointer
// is over the region or focus is inside it (WCAG 2.2.1; Android scales its
// timeout through the accessibility manager instead).

import { useEffect, useState, useSyncExternalStore, type FocusEvent, type ReactNode } from 'react'

import { byWindowClass } from '../../layout/byWindowClass'
import type { WindowSizeClass } from '../../layout/useWindowClass'
import { useWindowClassValue } from '../../layout/windowClassContext'
import { DURATION_MS, type SnackbarEntry } from './snackbarQueue'
import { dismissSnackbar, expireSnackbar, snackbarState, subscribeSnackbars } from './snackbarStore'
import { SnackbarSurface } from './SnackbarSurface'

/**
 * ADR 29.9.26al §4 — where the region sits per class. `start-24` is the `w-20`
 * rail plus a 1rem gutter; `start-[17rem]` is the `w-64` sidebar plus one. At
 * expanded, with no auto margins, the over-constrained box drops its end inset
 * and sits at `start` — bottom-start, M3's large-screen placement.
 */
export function snackbarPlacement(sizeClass: WindowSizeClass): string {
  return byWindowClass(sizeClass, {
    compact:
      'fixed inset-x-4 z-30 mx-auto flex max-w-xl flex-col gap-2 ' +
      'bottom-[calc(8.25rem+env(safe-area-inset-bottom))]',
    medium: 'fixed start-24 end-4 z-30 mx-auto flex max-w-xl flex-col gap-2 bottom-[4.25rem]',
    expanded: 'fixed start-[17rem] end-4 z-30 flex max-w-xl flex-col gap-2 bottom-[4.25rem]',
  })
}

export const SNACKBAR_LIVE_TESTID = 'snackbar-live'

/** Runs `current`'s timer unless the learner is holding it. */
function useExpiry(current: SnackbarEntry | null, held: boolean): void {
  const id = current?.id
  const duration = current?.duration
  useEffect(() => {
    if (id === undefined || duration === undefined || duration === 'indefinite' || held) return
    const timer = setTimeout(() => expireSnackbar(id), DURATION_MS[duration])
    return () => clearTimeout(timer)
  }, [id, duration, held])
}

export function SnackbarHost({ pinned }: { pinned?: ReactNode }) {
  const { current } = useSyncExternalStore(subscribeSnackbars, snackbarState)
  const { sizeClass } = useWindowClassValue()
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  useExpiry(current, hovered || focused)

  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false)
  }

  return (
    <div
      className={snackbarPlacement(sizeClass)}
      data-testid="snackbar-region"
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={onBlur}
    >
      {pinned}
      <div
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
        data-testid={SNACKBAR_LIVE_TESTID}
      >
        {current?.message ?? ''}
      </div>
      {current ? <CurrentEntry key={current.id} entry={current} /> : null}
    </div>
  )
}

function CurrentEntry({ entry }: { entry: SnackbarEntry }) {
  const action = entry.action
  return (
    <SnackbarSurface
      action={
        action
          ? {
              label: action.label,
              onPress: () => {
                action.perform()
                dismissSnackbar(entry.id)
              },
            }
          : undefined
      }
    >
      <span aria-hidden="true">{entry.message}</span>
    </SnackbarSurface>
  )
}
